<template>
    <v-app>
        <v-app-bar
            class="header"
            app
            dark
            flat
            clipped-right
        >
            <template v-if="isMobile($vuetify)">
                <div class="header-left">
                    <v-btn
                        v-if="route.query && route.query.from"
                        icon
                        @click="$router.push({ name: route.query.from }).catch((err) => {})"
                    >
                        <v-icon>far fa-arrow-left</v-icon>
                    </v-btn>

                    <connection-management-button class="ml-3" />
                </div>

                <div class="header-center">
                    <template v-if="!isDonePageOnly">
                        <v-btn
                            class="status-tab-btn"
                            depressed
                            outlined
                            @click="$router.push({ name: 'kitchenOpenTickets', query: route.query }).catch(err => {})"
                        >
                            <span class="mr-1">{{ openTicketsCount }}</span>

                            <v-icon>fal fa-folder-open</v-icon>
                        </v-btn>

                        <v-btn
                            class="status-tab-btn"
                            depressed
                            outlined
                            @click="$router.push({ name: 'kitchenDoneTickets', query: route.query }).catch(err => {})"
                        >
                            <span class="mr-1">{{ doneTicketsCount }}</span>
                            <v-icon>fal fa-folder</v-icon>
                        </v-btn>
                    </template>

                    <span v-else>{{ $t('general.kitchen') }}</span>
                </div>

                <div class="header-right">
                    <v-btn
                        class="ml-2"
                        icon
                        @click="drawerRef.drawer = !drawerRef.drawer"
                    >
                        <v-icon>far fa-bars</v-icon>
                    </v-btn>
                </div>
            </template>

            <template v-else>
                <div class="header-left">
                    <v-btn
                        v-if="route.query && route.query.from"
                        icon
                        @click="$router.push({ name: route.query.from }).catch((err) => {})"
                    >
                        <v-icon>far fa-arrow-left</v-icon>
                    </v-btn>

                    <v-btn
                        icon
                        @click.stop="refreshTickets"
                    >
                        <v-icon>far fa-redo</v-icon>
                    </v-btn>

                    <connection-management-button class="ml-3" />
                </div>

                <div class="header-center">
                    <v-btn
                        v-if="!isDonePageOnly"
                        class="status-tab-btn"
                        depressed
                        outlined
                        @click="$router.push({ name: 'kitchenOpenTickets', query: route.query }).catch(err => {})"
                    >
                        <span class="mr-1">{{ openTicketsCount }}</span>
                        <span>{{ $t('general.opened') }}</span>
                    </v-btn>

                    <v-btn
                        class="status-tab-btn"
                        depressed
                        outlined
                        @click="$router.push({ name: 'kitchenDoneTickets', query: route.query }).catch(err => {})"
                    >
                        <span class="mr-1">{{ doneTicketsCount }}</span>
                        <span>{{ $t('general.completed') }}</span>
                    </v-btn>
                </div>

                <div class="header-right">
                    <div
                        v-if="!isKDSTerminal && store.getters.user"
                        class="d-flex align-center mr-2"
                    >
                        <div
                            class="pa-3 cursor-pointer"
                            @click="setKdsSettings(!filterForUser)"
                        >
                            {{ $t('general.mine_only') }}
                        </div>

                        <v-switch
                            v-if="!isKDSTerminal && store.getters.user"
                            :input-value="filterForUser"
                            inset
                            hide-details
                            @change="setKdsSettings"
                        />
                    </div>

                    <div
                        v-if="!isDonePage && totalPages > 0"
                        class="paginate-btns"
                    >
                        <v-btn
                            v-for="(curPage, index) in totalPages"
                            :key="index"
                            large
                            text
                            class="mr-2"
                            :style="{
                                border: page === curPage ? 'solid 1px white' : 'solid 1px var(--v-secondary-base)',
                            }"
                            @click="setPage(curPage)"
                        >
                            <v-row no-gutters>
                                <v-col
                                    v-for="item in getItems(curPage)"
                                    :key="item.id"
                                    :cols="12 / gridCols"
                                >
                                    <div :class="getItemStatusClass(item)">-</div>
                                </v-col>
                            </v-row>
                        </v-btn>
                    </div>

                    <div v-else-if="totalPages > 0">
                        <v-btn
                            icon
                            :disabled="page <= 1"
                            @click="setPage('--')"
                        >
                            <v-icon>fal fa-angle-left</v-icon>
                        </v-btn>

                        <span>{{ page }}</span>

                        <v-btn
                            icon
                            :disabled="totalPages === page || totalPages === 0"
                            @click="setPage('++')"
                        >
                            <v-icon>fal fa-angle-right</v-icon>
                        </v-btn>
                    </div>

                    <v-btn
                        v-if="!isDonePage"
                        class="ml-2"
                        icon
                        @click="drawerRef.drawer = !drawerRef.drawer"
                    >
                        <v-icon>far fa-bars</v-icon>
                    </v-btn>
                </div>
            </template>
        </v-app-bar>

        <kitchen-drawer
            ref="drawerRef"
            class="drawer"
            :limited="isDonePageOnly"
        />

        <v-main class="overflow-hidden">
            <v-container
                class="content"
                fluid
            >
                <router-view />
            </v-container>
        </v-main>

        <v-footer
            v-if="isMobile($vuetify)"
            class="footer"
            app
            dark
        >
            <div class="footer-left">
                <div v-if="!isKDSTerminal && store.getters.user" class="mx-3">
                    <v-icon>fal fa-user</v-icon>
                </div>

                <v-switch
                    v-if="!isKDSTerminal && store.getters.user"
                    class="ma-0 pa-0"
                    :input-value="filterForUser"
                    inset
                    hide-details
                    @change="setKdsSettings"
                />
            </div>

            <div class="footer-center">
                <v-btn
                    icon
                    :disabled="page <= 1"
                    @click="setPage('--')"
                >
                    <v-icon>fal fa-angle-left</v-icon>
                </v-btn>

                <span>{{ page }}</span>

                <v-btn
                    icon
                    :disabled="totalPages === page || totalPages === 0"
                    @click="setPage('++')"
                >
                    <v-icon>fal fa-angle-right</v-icon>
                </v-btn>
            </div>

            <div class="footer-right">
                <v-btn
                    class="mr-3"
                    icon
                    @click.stop="refreshTickets"
                >
                    <v-icon>far fa-redo</v-icon>
                </v-btn>
            </div>
        </v-footer>
    </v-app>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, onBeforeMount } from 'vue';
import Terminal from '@/models/Terminal';
import UserService from '@/services/UserService';
import KitchenDrawer from './KitchenDrawer.vue';
import useKitchenTickets from './useKitchenTickets';
import useRouter from '@/utilities/useRouter';
import ConnectionManagementButton from '@/components/ConnectionManagementButton.vue';
import { useAppStore } from '@/store/useAppStore';
import * as mutationTypes from '@/store/mutationTypes';

const store = useAppStore();
const { route } = useRouter();

const {
    openTickets,
    doneTickets,
    openTicketsCount,
    doneTicketsCount,
    gridCols,
    openPage,
    donePage,
    totalOpenPages,
    totalDonePages,
    fetchLatest,
    clearFetchLatestInterval,
    refreshTickets,
} = useKitchenTickets();

const drawerRef = ref(null);

const isDonePage = computed(() => route.value.name === 'kitchenDoneTickets');
const filterForUser = computed(() => store.getters.kdsSettings.filterForUser)
const isDonePageOnly = ref(false);
const isKDSTerminal = ref(false);

const page = computed(() => (isDonePage.value ? donePage.value : openPage.value));

const totalPages = computed(() => (isDonePage.value ? totalDonePages.value : totalOpenPages.value));

const setPage = (newPage) => {
    if (newPage === '--') {
        if (isDonePage.value) {
            donePage.value -= 1;
            openPage.value = 1;
        } else {
            openPage.value -= 1;
            donePage.value = 1;
        }
    } else if (newPage === '++') {
        if (isDonePage.value) {
            donePage.value += 1;
            openPage.value = 1;
        } else {
            openPage.value += 1;
            donePage.value = 1;
        }
    } else {
        if (isDonePage.value) {
            donePage.value = newPage;
            openPage.value = 1;
        } else {
            openPage.value = newPage;
            donePage.value = 1;
        }
    }
};

const getItems = (page) => {
    const items = route.value.name === 'kitchenDoneTickets' ? doneTickets.value : openTickets.value;

    return items.filter((order) => order.page === page);
};

const setKdsSettings = filterForUser => {
    const kdsSettings = { ...store.getters.kdsSettings, filterForUser }
    AppStorage.local().setItem('kds_settings', JSON.stringify(kdsSettings));
    store.commit(mutationTypes.SET_KDS_SETTINGS, { kdsSettings });
};

const getItemStatusClass = (item) => {
    let itemClass = 'grey--text';

    if (item.warningColor === 'yellow') {
        itemClass = 'amber--text';
    }

    if (item.warningColor === 'red') {
        itemClass = 'red--text';
    }

    return itemClass;
};

const isMobile = (vuetify) => {
    return vuetify.breakpoint.xs || vuetify.breakpoint.sm;
};

onBeforeMount(() => {
    const kdsSettings = JSON.parse(AppStorage.local().getItem('kds_settings'));

    if (kdsSettings) {
        store.commit(mutationTypes.SET_KDS_SETTINGS, { kdsSettings });
    }
});

onMounted(async () => {
    const terminal = await Terminal.current();
    isKDSTerminal.value = terminal?.isKitchenDisplaySystem();
    isDonePageOnly.value = !isKDSTerminal.value && !UserService.allows('access_terminals');

    fetchLatest();
});

onUnmounted(() => {
    clearFetchLatestInterval();
});
</script>

<style lang="scss" scoped>
.header {
    background-color: #101219 !important;

    .header-left {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        flex: 1;
    }

    .header-center {
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 1;
    }

    .header-right {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        flex: 1;
    }

    @media screen and (max-width: 600px) {
        ::v-deep(.v-toolbar__content) {
            padding: 4px;
        }
    }

    .status-tab-btn {
        min-width: 150px;
        margin: 0 4px;

        @media screen and (max-width: 600px) {
            padding: 0 8px;
            min-width: 66px;
        }
    }

    .paginate-btns {
        display: flex;
        justify-content: flex-end;
        min-width: 105px;
    }
}

.content {
    background-color: #101219 !important;
    height: 100%;
    overflow: hidden !important;
    padding: 0px 4px;
}

.drawer {
    background-color: #101219 !important;
}

.footer {
    padding-right: 0;
    padding-left: 0;
    display: flex;
    align-items: center;
    background-color: #1d1f29 !important;
    border: none;

    .footer-left {
        display: flex;
        justify-content: flex-start;
        align-items: center;
        flex: 1;
    }

    .footer-center {
        display: flex;
        justify-content: center;
        align-items: center;
        flex: 1;
    }

    .footer-right {
        display: flex;
        justify-content: flex-end;
        align-items: center;
        flex: 1;
    }
}
</style>
